import { useState, useRef, useEffect } from 'react'
import { PLAN_COLORS } from '../hooks/useTravelingPlan'

export function CreatePlanModal({ onClose, onCreate, existingPlan = null }) {
    const [planName, setPlanName] = useState(existingPlan?.name || '')
    const [selectedColor, setSelectedColor] = useState(existingPlan?.color || PLAN_COLORS[0])
    const [error, setError] = useState('')
    const inputRef = useRef(null)

    // Focus input on mount
    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }
        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [onClose])

    const handleSubmit = (e) => {
        e.preventDefault()

        const trimmedName = planName.trim()

        if (!trimmedName) {
            setError('Plan name is required')
            return
        }

        if (trimmedName.length > 50) {
            setError('Plan name must be 50 characters or less')
            return
        }

        onCreate(trimmedName, selectedColor)
        onClose()
    }

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ zIndex: 99999 }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose()
            }}
        >
            <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full mx-4">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-xl font-semibold text-gray-900">
                        {existingPlan ? 'Edit Plan' : 'Create New Plan'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Close"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Plan Name Input */}
                    <div className="mb-5">
                        <label
                            htmlFor="plan-name"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Plan Name
                        </label>
                        <input
                            ref={inputRef}
                            type="text"
                            id="plan-name"
                            value={planName}
                            onChange={(e) => {
                                setPlanName(e.target.value)
                                setError('')
                            }}
                            placeholder="e.g., Weekend Trip, Birthday Ideas"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                            maxLength={50}
                        />
                        {error && (
                            <p className="mt-2 text-sm text-red-600">{error}</p>
                        )}
                        <p className="mt-1.5 text-xs text-gray-500">
                            {planName.length}/50 characters
                        </p>
                    </div>

                    {/* Color Picker */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Plan Color
                        </label>
                        <div className="grid grid-cols-8 gap-2.5">
                            {PLAN_COLORS.map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    onClick={() => setSelectedColor(color)}
                                    className={`
                                        w-10 h-10 rounded-full transition-all
                                        ${selectedColor === color
                                            ? 'ring-2 ring-offset-2 ring-gray-400 scale-110'
                                            : 'hover:scale-105'
                                        }
                                    `}
                                    style={{ backgroundColor: color }}
                                    aria-label={`Select color ${color}`}
                                >
                                    {selectedColor === color && (
                                        <svg
                                            className="w-6 h-6 text-white mx-auto drop-shadow"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            {existingPlan ? 'Save Changes' : 'Create Plan'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}
